import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { Filter } from "lucide-react";

export default function Catalogue() {
  const [sp, setSp] = useSearchParams();
  const { slug } = useParams();
  const [products, setProducts] = useState([]);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const q = sp.get("q") || "";
  const sort = sp.get("sort") || "newest";
  const minP = sp.get("min") || "";
  const maxP = sp.get("max") || "";
  const category = sp.get("category") || "";

  useEffect(() => { api.get("/categories").then((r) => setCats(r.data)); }, []);

  const activeCat = useMemo(() => {
    if (slug) return cats.find((c) => c.slug === slug)?.name;
    return category;
  }, [slug, category, cats]);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (q) params.q = q;
    if (activeCat) params.category = activeCat;
    if (minP) params.min_price = minP;
    if (maxP) params.max_price = maxP;
    if (sort) params.sort = sort;
    api.get("/products", { params }).then((r) => setProducts(r.data)).finally(() => setLoading(false));
  }, [q, activeCat, minP, maxP, sort]);

  const upd = (k, v) => {
    const n = new URLSearchParams(sp);
    if (v) n.set(k, v); else n.delete(k);
    setSp(n);
  };

  return (
    <div className="blz-container py-10">
      <div className="mb-6">
        <div className="text-xs font-bold uppercase tracking-wider text-emerald-700">Catalogue</div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-900">{activeCat || "All medications"}</h1>
        {q && <p className="text-sm text-slate-500 mt-1">Showing results for "<span className="font-mono">{q}</span>"</p>}
      </div>
      <div className="grid lg:grid-cols-4 gap-6">
        <aside className="lg:col-span-1">
          <div className="card-flat p-5 sticky top-20">
            <div className="flex items-center gap-2 font-semibold text-slate-900 mb-4"><Filter className="h-4 w-4"/> Filters</div>
            <div className="mb-4">
              <div className="text-xs font-semibold text-slate-700 mb-2">Category</div>
              <select className="w-full h-10 rounded-lg border border-slate-200 px-3 text-sm bg-white" value={category} onChange={(e) => upd("category", e.target.value)} data-testid="filter-category">
                <option value="">All categories</option>
                {cats.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
              </select>
            </div>
            <div className="mb-4">
              <div className="text-xs font-semibold text-slate-700 mb-2">Price</div>
              <div className="flex gap-2">
                <input placeholder="Min" type="number" value={minP} onChange={(e) => upd("min", e.target.value)} className="w-full h-10 rounded-lg border border-slate-200 px-3 text-sm" data-testid="filter-min-price"/>
                <input placeholder="Max" type="number" value={maxP} onChange={(e) => upd("max", e.target.value)} className="w-full h-10 rounded-lg border border-slate-200 px-3 text-sm" data-testid="filter-max-price"/>
              </div>
            </div>
            <div className="mb-4">
              <div className="text-xs font-semibold text-slate-700 mb-2">Sort by</div>
              <select className="w-full h-10 rounded-lg border border-slate-200 px-3 text-sm bg-white" value={sort} onChange={(e) => upd("sort", e.target.value)} data-testid="filter-sort">
                <option value="newest">Newest</option>
                <option value="price_asc">Price: Low to High</option>
                <option value="price_desc">Price: High to Low</option>
                <option value="name">Name: A - Z</option>
              </select>
            </div>
          </div>
        </aside>
        <div className="lg:col-span-3">
          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {Array.from({length: 6}).map((_,i) => <div key={i} className="aspect-[3/4] rounded-2xl bg-slate-100 animate-pulse"/>)}
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-20 text-slate-500">
              <div className="text-xl font-semibold text-slate-900">No products found</div>
              <div className="text-sm mt-1">Try a different search or clear filters.</div>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-testid="catalogue-grid">
              {products.map((p) => <ProductCard key={p.id} product={p}/>)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
